import React, {useState, useEffect, useCallback} from 'react';
import {useContext, useContextSelector} from 'use-context-selector';

import useCreateClientStatusMessage from './util/useCreateClientStatusMessage';
import i18n from './i18n';
import useInterval from './util/useInterval';
import createUUID from './util/createUUID';
import Loader from './common/Loader';

import initialLocalData from './context/initialLocalData';

import {LocalContext} from './context/LocalContext';
import {SessionContext} from './context/SessionContext';

import initialSessionData from './context/initialSessionData';

import LayoutComponents from './common/LayoutComponents';
import RoutedComponents from './common/RoutedComponents';
import WrappingComponents from './common/WrappingComponents';

console.log(`REACT_APP_BUILDDATE: ${process.env.REACT_APP_BUILDDATE.toString()}`);

function App() {

    const [localData, setLocalData] = useContext(LocalContext);
    const [sessionData, setSessionData] = useContext(SessionContext);

    const clientOnline = useContextSelector(LocalContext, v => v[0].clientOnline);

    const modeRegistration = useContextSelector(SessionContext, v => v[0].modeRegistration);
    const modeLogin = useContextSelector(SessionContext, v => v[0].modeLogin);
    const userAuthenticated = useContextSelector(LocalContext, v => v[0].user.authenticated);

    const createClientStatusMessage = useCreateClientStatusMessage();

    const [isConnectingSetNetworkStatusSocket, setIsConnectingSetNetworkStatusSocket] = useState(false);
    const [isSetNetworkStatusSocket, setIsSetNetworkStatusSocket] = useState(false);

    const [isErrorSetNetworkStatusSocket, setIsErrorSetNetworkStatusSocket] = useState(false);
    const [networkStatusSocket, setNetworkStatusSocket] = useState(null)

    const [connectionAttemptsSetNetworkStatusSocket, setConnectionAttemptsSetNetworkStatusSocket] = useState(0);

    const handleSetNetworkStatusSocket = useCallback(() => {
        if (!isConnectingSetNetworkStatusSocket) {
            setIsConnectingSetNetworkStatusSocket(true)
            setConnectionAttemptsSetNetworkStatusSocket(connectionAttemptsSetNetworkStatusSocket + 1);

            const socket = new WebSocket(`${process.env.REACT_APP_NETWORK_POLLING_WS_URL}/${createUUID()}/`);

            const onopen = (event) => {
                setIsSetNetworkStatusSocket(true);
                setIsConnectingSetNetworkStatusSocket(false);
                setIsErrorSetNetworkStatusSocket(false);
                setConnectionAttemptsSetNetworkStatusSocket(0);
                setNetworkStatusSocket(socket);

                if (!clientOnline) {
                    setLocalData({...localData, clientOnline: true});
                }
            }

            socket.addEventListener('open', onopen);

            const onerror = (event) => {
                if (clientOnline) {
                    setLocalData({...localData, clientOnline: false});
                }
                setIsConnectingSetNetworkStatusSocket(false);
                if (!isSetNetworkStatusSocket) {
                    setIsErrorSetNetworkStatusSocket(true);
                    setNetworkStatusSocket(null);
                }
                socket.removeEventListener('error', onerror)
                socket.removeEventListener('open', onopen)
            }

            socket.addEventListener('error', onerror);
        }
    }, [
        isConnectingSetNetworkStatusSocket,
        setIsConnectingSetNetworkStatusSocket,
        setConnectionAttemptsSetNetworkStatusSocket,
        connectionAttemptsSetNetworkStatusSocket,
        isSetNetworkStatusSocket,
        setIsErrorSetNetworkStatusSocket,
        setIsSetNetworkStatusSocket,
        setNetworkStatusSocket,
        clientOnline,
        localData,
        setLocalData
    ]);

    useEffect(() => {
        if (
            process.env.REACT_APP_MAINTENANCE_MODE !== 'true'
            && !isSetNetworkStatusSocket
            && !networkStatusSocket
            && !isConnectingSetNetworkStatusSocket
            && connectionAttemptsSetNetworkStatusSocket <= 2
        ) {
            handleSetNetworkStatusSocket()
        }
    }, [
        handleSetNetworkStatusSocket,
        setIsConnectingSetNetworkStatusSocket,
        connectionAttemptsSetNetworkStatusSocket,
        networkStatusSocket,
        isSetNetworkStatusSocket,
        isConnectingSetNetworkStatusSocket
    ])

    useInterval(() => {
        if (
            !clientOnline
            && !isSetNetworkStatusSocket
            && connectionAttemptsSetNetworkStatusSocket > 2
            && isErrorSetNetworkStatusSocket
            && !isConnectingSetNetworkStatusSocket
        ) {
            if (!document.hidden) {
                setConnectionAttemptsSetNetworkStatusSocket(0);
            }
        }
    }, 5000)

    useInterval(() => {
        if (networkStatusSocket !== null && !isConnectingSetNetworkStatusSocket) {
            if (networkStatusSocket.readyState === 3) {
                handleSetNetworkStatusSocket();
            }
        }
    }, 3000)

    useEffect(() => {
        if ((modeLogin || modeRegistration) && userAuthenticated) {
            setSessionData({
                ...sessionData,
                modalVisible: false,
                modeLogin: false,
                modeRegistration: false,
                toReset: false
            })
        }
    }, [modeLogin, modeRegistration, sessionData, setSessionData, userAuthenticated]);

    const cookiesPolicyExp = useContextSelector(LocalContext, v => v[0].cookiesPolicy.exp);
    const cookiesPolicyAccepted = useContextSelector(LocalContext, v => v[0].cookiesPolicy.accepted);
    useEffect(() => {
        if (new Date(cookiesPolicyExp) > new Date() && !cookiesPolicyAccepted) {
            setLocalData({
                ...localData,
                cookiesPolicy: {
                    exp: cookiesPolicyExp,
                    accepted: true
                }
            });

            setSessionData({
                ...sessionData,
                modalVisible: false
            });

        };
    }, [sessionData, setSessionData, setLocalData, cookiesPolicyExp, cookiesPolicyAccepted, localData]);

    const UILangGuest = useContextSelector(LocalContext, v => v[0].UILang);
    const UILangSession = useContextSelector(SessionContext, v => v[0].UILang);

    const handleUILang = useCallback(() => {
        if (cookiesPolicyAccepted) {
            if (UILangGuest !== i18n.language) {
                i18n.changeLanguage(UILangGuest);
            }
        } else {
            if (UILangSession !== i18n.language) {
                i18n.changeLanguage(UILangSession);
            }
        }
    }, [cookiesPolicyAccepted, UILangGuest, UILangSession]);

    useEffect(() => {
        i18n.on('initialized', (options) => {
            handleUILang();
        });
    }, [handleUILang]);

    useEffect(() => {
        if (i18n.isInitialized) {
            handleUILang();
        }
    }, [handleUILang]);

    useEffect(() => {
        i18n.on('failedLoading', (lng, ns, msg) => {
            if (!document.visibilityState !== 'hidden') {
                createClientStatusMessage({
                    variant: 'info',
                    i18nKey: 'error.i18n-failedLoading',
                    message: "Application Error: Could not load localization data.",
                    source: `i18next: ${msg}`
                });
            }
        })
    }, [localData, setLocalData, createClientStatusMessage]);

    const [maintenanceModeStateReset, setMaintenanceModeStateReset] = useState(false);

    useEffect(() => {
        if (!maintenanceModeStateReset && process.env.REACT_APP_MAINTENANCE_MODE === 'true') {
            setMaintenanceModeStateReset(true);
            setLocalData({
                ...initialLocalData,
                UILang: localData.UILang,
            });
        }
    }, [localData, maintenanceModeStateReset, setLocalData]);

    const [clientStatusMessagesCleared, setClientStatusMessagesCleared] = useState(false);

    useEffect(() => {
        if (!clientStatusMessagesCleared) {
            setClientStatusMessagesCleared(true);
            setLocalData({...localData, clientStatusMessages: []});
        }
    }, [localData, setLocalData, clientStatusMessagesCleared]);

    const [clientStorageRevChecked, setClientStorageRevChecked] = useState(false);

    const clientStorageRev = useContextSelector(LocalContext, v => v[0].clientStorageRev);

    useEffect(() => {
        if (!clientStorageRevChecked) {
            setClientStorageRevChecked(true);
            if (process.env.REACT_APP_BUILDDATE !== localData.clientBuildDate) {
                setLocalData({
                    ...initialLocalData,
                    UILang: localData.UILang,
                    cookiesPolicy: localData.cookiesPolicy,
                    clientBuildDate: process.env.REACT_APP_BUILDDATE,
                });
            }
        }
    }, [localData, setLocalData, clientStorageRevChecked, clientStorageRev]);

    useEffect(() => {
        if (localData.toReset) {
            setLocalData({
                ...initialLocalData,
            });
            setSessionData({
                ...initialSessionData,
            });
        }
    }, [localData, setLocalData, setSessionData]);

    return <>
        <Loader>
            <WrappingComponents>
                <LayoutComponents>
                    <RoutedComponents />
                </LayoutComponents>
            </WrappingComponents>
        </Loader>
    </>
}

export default App;
